import { ChangeDetectorRef, Directive, inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { ControlValueAccessor, FormControl, FormControlDirective, FormControlName, FormGroupDirective, NgControl, NgModel } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

@Directive({ standalone: true })
export class SvzControl implements ControlValueAccessor, OnInit, OnDestroy {
  public control = new FormControl<unknown | null>(null);
  public readonly disabled = signal<boolean>(false);
  public value: unknown = null;

  protected readonly destroy$ = new Subject<void>();
  protected readonly injector = inject(Injector);
  protected readonly cdr = inject(ChangeDetectorRef);

  private initialControl?: FormControl<unknown | null>;
  private isApplyingValue = false;

  public onChange: (newValue: unknown) => void = () => undefined;
  public onTouched: () => void = () => undefined;

  ngOnInit(): void {
    this.setComponentControl();
  }

  writeValue(value: unknown): void {
    this.value = value;
    // `emitModelToViewChange: false` is required here — without it, `setValue` calls straight back
    // into this same `writeValue` (that's how Angular syncs the model back to the value accessor),
    // which recurses until the call stack overflows. See RCA Type Registration redesign.
    this.control.setValue(value, { emitEvent: false, emitModelToViewChange: false });
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  enable(): void {
    if (this.control && this.control.disabled) {
      this.control.enable();
    }
    this.disabled.set(false);
  }

  disable(): void {
    if (this.control && this.control.enabled) {
      this.control.disable();
    }
    this.disabled.set(true);
  }

  protected onValueChanged(_value: unknown): void {}

  protected onControlChanged(_previousControl?: FormControl<unknown | null>): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setComponentControl(): void {
    let injectedControl: NgControl | null = null;

    try {
      injectedControl = this.injector.get(NgControl, null);
    } catch {
      return;
    }

    if (!injectedControl) {
      return;
    }

    let nextControl: FormControl<unknown | null> | null = null;

    if (injectedControl instanceof NgModel) {
      nextControl = injectedControl.control as FormControl<unknown | null>;
    } else if (injectedControl instanceof FormControlName) {
      const formGroup = this.injector.get(FormGroupDirective, null);
      nextControl = formGroup?.getControl(injectedControl) as FormControl<unknown | null> | null;
    } else if (injectedControl instanceof FormControlDirective) {
      nextControl = injectedControl.form as FormControl<unknown | null> | null;
    }

    if (!nextControl || nextControl === this.initialControl) {
      return;
    }

    const previousControl = this.initialControl;
    this.initialControl = nextControl;
    this.control = nextControl;
    this.value = nextControl.value;
    this.disabled.set(nextControl.disabled);

    this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.value = value;
      if (!this.isApplyingValue) {
        this.onValueChanged(value);
      }
      this.cdr.markForCheck();
    });

    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const isDisabled = this.control.disabled;
      if (this.disabled() !== isDisabled) {
        this.disabled.set(isDisabled);
      }
    });

    this.onControlChanged(previousControl);
    this.cdr.markForCheck();
  }
}
